import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Toolbar,
  Typography,
  Box,
  Chip,
} from '@mui/material';
import {
  Dashboard,
  People,
  Group,
  Assessment,
  Phone,
  Settings,
  Webhook,
  TrendingUp,
  PersonAdd,
  Assignment,
  AccountTree,
  LocalHospital,
} from '@mui/icons-material';

const drawerWidth = 240;

const Sidebar = ({ open, onToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);

  const getMenuItems = () => {
    const baseItems = [
      { text: 'Dashboard', icon: <Dashboard />, path: '/dashboard' },
    ];

    if (user?.role === 'admin') {
      return [
        ...baseItems,
        { text: 'All Leads', icon: <People />, path: '/leads' },
        { text: 'Users', icon: <Group />, path: '/users' },
        { text: 'Lead Flow', icon: <AccountTree />, path: '/lead-flow' },
        { text: 'IPD Done', icon: <LocalHospital />, path: '/ipd-done' },
        { text: 'Reports', icon: <Assessment />, path: '/reports' },
        { text: 'Webhooks', icon: <Webhook />, path: '/webhooks' },
        { text: 'Call Logs', icon: <Phone />, path: '/calls' },
        { text: 'Analytics', icon: <TrendingUp />, path: '/analytics' },
        { text: 'Settings', icon: <Settings />, path: '/settings' },
      ];
    }

    if (user?.role === 'manager') {
      return [
        ...baseItems,
        { text: 'My Leads', icon: <People />, path: '/leads' },
        { text: 'Team Members', icon: <Group />, path: '/users' },
        { text: 'Create User', icon: <PersonAdd />, path: '/users/create' },
        { text: 'Lead Flow', icon: <AccountTree />, path: '/lead-flow' },
        { text: 'IPD Done', icon: <LocalHospital />, path: '/ipd-done' },
        { text: 'Reports', icon: <Assessment />, path: '/reports' },
        { text: 'Call Logs', icon: <Phone />, path: '/calls' },
        { text: 'Performance', icon: <TrendingUp />, path: '/performance' },
      ];
    }

    if (user?.role === 'team_lead') {
      return [
        ...baseItems,
        { text: 'My Leads', icon: <People />, path: '/leads' },
        { text: 'My Team', icon: <Group />, path: '/users' },
        { text: 'Create BDA', icon: <PersonAdd />, path: '/users/create' },
        { text: 'Lead Flow', icon: <AccountTree />, path: '/lead-flow' },
        { text: 'IPD Done', icon: <LocalHospital />, path: '/ipd-done' },
        { text: 'Assignments', icon: <Assignment />, path: '/assignments' },
        { text: 'Call Logs', icon: <Phone />, path: '/calls' },
        { text: 'Performance', icon: <TrendingUp />, path: '/performance' },
      ];
    }

    // BDA role
    return [
      ...baseItems,
      { text: 'My Leads', icon: <People />, path: '/leads' },
      { text: 'IPD Done', icon: <LocalHospital />, path: '/ipd-done' },
      { text: 'Call Logs', icon: <Phone />, path: '/calls' },
      { text: 'My Performance', icon: <TrendingUp />, path: '/performance' },
    ];
  };

  const menuItems = getMenuItems();

  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        width: open ? drawerWidth : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          boxSizing: 'border-box',
          backgroundColor: '#f5f5f5',
          borderRight: '1px solid #e0e0e0',
        },
      }}
    >
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            CRM
          </Typography>
          <Chip
            label={user?.role?.toUpperCase()}
            size="small"
            color="primary"
            variant="outlined"
          />
        </Box>
      </Toolbar>

      <Divider />

      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => navigate(item.path)}
              sx={{
                '&.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': {
                    color: 'primary.contrastText',
                  },
                },
                '&:hover': {
                  backgroundColor: 'primary.light',
                  opacity: 0.8,
                },
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Drawer>
  );
};

export default Sidebar;